import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { Celestial as CelestialKind } from "@/data/phases";

const SIZE = 184;
const R = 40; // disc radius in the -50…50 viewBox

function Sun({ tone }: { tone: string }) {
  const reduced = useReducedMotion();
  return (
    <div style={{ position: "relative", width: SIZE, height: SIZE }}>
      <motion.svg
        width={SIZE}
        height={SIZE}
        viewBox="-50 -50 100 100"
        style={{ position: "absolute", inset: 0 }}
        animate={reduced ? undefined : { rotate: 360 }}
        transition={reduced ? undefined : { duration: 90, ease: "linear", repeat: Infinity }}
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <rect key={i} x={-1.4} y={-49} width={2.8} height={12} rx={1.4} fill={tone} fillOpacity={0.45} transform={`rotate(${i * 30})`} />
        ))}
      </motion.svg>
      <motion.div
        style={{
          position: "absolute",
          inset: 30,
          borderRadius: "50%",
          background: `radial-gradient(circle at 38% 34%, #fffaf0, ${tone} 62%, ${tone} 100%)`,
          boxShadow: `0 0 24px 2px ${tone}66, inset 0 0 16px rgba(255,255,255,.5)`,
        }}
        animate={reduced ? undefined : { scale: [1, 1.04, 1] }}
        transition={reduced ? undefined : { duration: 6, ease: "easeInOut", repeat: Infinity }}
      />
    </div>
  );
}

function Moon({ tone }: { tone: string }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      style={{ position: "relative", width: SIZE, height: SIZE }}
      animate={reduced ? undefined : { scale: [1, 1.03, 1] }}
      transition={reduced ? undefined : { duration: 7, ease: "easeInOut", repeat: Infinity }}
    >
      <div style={{ position: "absolute", inset: 22, borderRadius: "50%", boxShadow: `0 0 28px 2px ${tone}55` }}>
        <svg viewBox="-50 -50 100 100" width="100%" height="100%" style={{ display: "block" }}>
          <defs>
            <radialGradient id="tc-moon-lit" cx="38%" cy="32%" r="75%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="58%" stopColor={tone} />
              <stop offset="100%" stopColor="#c2cbe0" />
            </radialGradient>
          </defs>
          <circle r={R} fill="url(#tc-moon-lit)" />
          {/* craters */}
          <circle cx={-13} cy={-11} r={6.5} fill="rgba(120,132,160,.26)" />
          <circle cx={9} cy={6} r={9} fill="rgba(120,132,160,.2)" />
          <circle cx={-9} cy={15} r={4.5} fill="rgba(120,132,160,.24)" />
          <circle r={R} fill="none" stroke={tone} strokeOpacity={0.35} strokeWidth={1} />
        </svg>
      </div>
    </motion.div>
  );
}

/** Sun or moon, crossfading directly behind the avatar (the photo sits on top). */
export function Celestial({ kind, tone }: { kind: CelestialKind; tone: string }) {
  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        // biased up a touch so the larger disc peeks over the head, not the role text below
        transform: "translate(-50%, calc(-50% - 16px))",
        zIndex: 0,
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={kind}
          style={{ position: "absolute", top: -SIZE / 2, left: -SIZE / 2 }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
        >
          {kind === "sun" ? <Sun tone={tone} /> : <Moon tone={tone} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
